import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { useVenue } from "../VenueProvider";
import { Section, ACCENT, formatMXN, CustomTooltip } from "../cuentas/shared";
import { Kpi, VALOR_COLOR } from "./shared";
import { variacion, resumenClientes } from "../../utils/reportes";
import { formatCalendarDate } from "../../utils/dates";
import {
  UsersIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/20/solid";

// Quién te deja el dinero. Tres lecturas: el ranking del período, cuántos
// repiten, y cuánto te deben entre todos.
//
// "Nuevo" y "recurrente" se miden por historia completa, no por período:
// un cliente que te compra una boda al año no es nuevo cada vez.
const ReporteClientes = ({ rango }) => {
  const navigate = useNavigate();
  const { clientes, events, payments, calendarTimezone: tz } = useVenue();
  const [verTodos, setVerTodos] = useState(false);

  const actual = useMemo(
    () =>
      resumenClientes({
        clientes,
        events,
        payments,
        desde: rango.from,
        hasta: rango.to,
        timeZone: tz,
      }),
    [clientes, events, payments, rango, tz]
  );

  const previo = useMemo(
    () =>
      rango.prev
        ? resumenClientes({
            clientes,
            events,
            payments,
            desde: rango.prev.from,
            hasta: rango.prev.to,
            timeZone: tz,
          })
        : null,
    [clientes, events, payments, rango, tz]
  );

  const activos = actual.filas.filter((f) => f.eventosPeriodo > 0);
  const topGrafica = activos.slice(0, 8).map((f) => ({
    nombre: f.nombre.length > 18 ? `${f.nombre.slice(0, 17)}…` : f.nombre,
    valor: f.valorPeriodo,
    id: f.id,
  }));

  // La tabla lista a los del período; "ver todos" abre el padrón completo,
  // que es donde aparece el que no te ha comprado en dos años.
  const filas = verTodos ? actual.filas : activos;

  const delta = (campo) =>
    previo ? variacion(actual.totales[campo], previo.totales[campo]) : null;

  if (actual.totales.clientes === 0) {
    return (
      <Section icon={UsersIcon} title="Clientes">
        <p className="text-sm text-zinc-400">
          Todavía no hay clientes. Se crean solos al capturar un
          evento o al convertir un lead, y también puedes darlos de alta en
          Directorio → Clientes.
        </p>
      </Section>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi
          label="Con evento"
          value={actual.totales.activos}
          delta={delta("activos")}
          hint={`de ${actual.totales.clientes} en el directorio`}
        />
        <Kpi
          label="Nuevos"
          value={actual.totales.nuevos}
          delta={delta("nuevos")}
          hint="su primer evento contigo"
        />
        <Kpi
          label="Recurrentes"
          value={actual.totales.recurrentes}
          delta={delta("recurrentes")}
          hint={`${actual.totales.conRecurrencia} han repetido alguna vez`}
        />
        <Kpi
          label="Ticket promedio"
          value={formatMXN(actual.totales.ticketPromedio)}
          delta={delta("ticketPromedio")}
          hint={`${actual.totales.eventosPeriodo} ${
            actual.totales.eventosPeriodo === 1 ? "evento" : "eventos"
          } del período`}
        />
        <Kpi
          label="Te deben"
          value={formatMXN(actual.totales.debe)}
          hint="saldo vivo de todos sus eventos"
          tone={actual.totales.debe > 0 ? "text-amber-600" : ""}
        />
      </div>

      {actual.cobertura.sinFicha > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10 p-4">
          <div className="flex gap-2.5">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-600" />
            <div className="text-sm text-amber-800 dark:text-amber-200">
              <span className="font-semibold">
                {actual.cobertura.sinFicha} de {actual.cobertura.eventos} eventos
                del período no tienen cliente ligado
              </span>{" "}
              — no cuentan en ninguna cifra de este reporte. Desde la página de
              un cliente puedes ligarle los eventos que coincidan por correo o
              teléfono.
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <Section icon={ChartBarIcon} title="Clientes con mayor valor en el período">
          {topGrafica.length === 0 ? (
            <p className="text-sm text-zinc-400">
              Ningún cliente con evento en este período.
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(180, topGrafica.length * 34)}>
              <BarChart
                data={topGrafica}
                layout="vertical"
                margin={{ top: 4, right: 16, left: 4, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} className="stroke-zinc-200 dark:stroke-zinc-700" />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => formatMXN(v)} />
                <YAxis
                  type="category"
                  dataKey="nombre"
                  width={110}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="valor" name="Valor" radius={[0, 4, 4, 0]}>
                  {topGrafica.map((f) => (
                    <Cell key={f.id} fill={VALOR_COLOR} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Section>

        <Section
          icon={UsersIcon}
          title={verTodos ? "Todos los clientes" : "Clientes del período"}
          action={
            <button
              type="button"
              onClick={() => setVerTodos((v) => !v)}
              className="text-xs font-semibold hover:underline"
              style={{ color: ACCENT }}
            >
              {verTodos ? "Solo los del período" : "Ver todos"}
            </button>
          }
        >
          {filas.length === 0 ? (
            <p className="text-sm text-zinc-400">Sin clientes que mostrar.</p>
          ) : (
            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-zinc-400">
                    <th className="py-2 pl-1 font-semibold">Cliente</th>
                    <th className="py-2 font-semibold text-right">Eventos</th>
                    <th className="py-2 font-semibold text-right">
                      {verTodos ? "Valor total" : "Valor"}
                    </th>
                    <th className="py-2 font-semibold text-right">Debe</th>
                    <th className="py-2 pr-1 font-semibold text-right">Último</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/50">
                  {filas.map((f) => (
                    <tr key={f.id}>
                      <td className="py-2 pl-1">
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`/dashboard/clientes/${f.id}`, {
                              state: { from: "reportes", reporte: "clientes" },
                            })
                          }
                          className="hover:underline text-left truncate max-w-[180px]"
                          style={{ color: ACCENT }}
                          title={f.nombre}
                        >
                          {f.nombre}
                        </button>
                        {f.recurrente && (
                          <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-600">
                            repite
                          </span>
                        )}
                      </td>
                      <td className="py-2 text-right text-zinc-500 whitespace-nowrap">
                        {verTodos ? f.eventos : f.eventosPeriodo}
                      </td>
                      <td className="py-2 text-right font-medium text-zinc-900 dark:text-white whitespace-nowrap">
                        {formatMXN(verTodos ? f.valorTotal : f.valorPeriodo)}
                      </td>
                      <td
                        className={`py-2 text-right whitespace-nowrap ${
                          f.debe > 0 ? "text-amber-600 font-medium" : "text-zinc-400"
                        }`}
                      >
                        {f.debe > 0 ? formatMXN(f.debe) : "—"}
                      </td>
                      <td className="py-2 pr-1 text-right text-zinc-500 whitespace-nowrap">
                        {f.ultimoEvento
                          ? formatCalendarDate(f.ultimoEvento, tz, { style: "short" })
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      </div>
    </div>
  );
};

export default ReporteClientes;
